import React from 'react';
import { Box, Typography, Avatar, Checkbox } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { ExpertEvent } from './types';
import { CATEGORY_META } from './constants';

interface ExpertEventRowProps {
  event: ExpertEvent;
  onClick: () => void;
  onToggleComplete: (isCompleted: boolean) => void;
}

export const ExpertEventRow: React.FC<ExpertEventRowProps> = ({ event, onClick, onToggleComplete }) => {
  const meta = CATEGORY_META[event.type];
  const { color, name } = event.expert;
  const details = event.raw?.details as any;
  const rawInput: string | undefined = details?.rawInput;

  return (
    <Box sx={{
      display: 'flex', flexDirection: 'column', justifyContent: 'center', py: 1.25, px: 1,
      minHeight: 56,
      borderRadius: 1,
      borderLeft: `3px solid ${color}`,
      bgcolor: alpha(color, 0.05),
      opacity: event.completed ? 0.6 : 1,
      cursor: 'pointer',
      '&:hover': { filter: 'brightness(0.97)' },
    }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
        <Checkbox
          checked={event.completed}
          onChange={(e) => { 
            e.stopPropagation(); 
            onToggleComplete(e.target.checked); 
          }}
          onClick={(e) => e.stopPropagation()}
          size="small"
          sx={{ flexShrink: 0, p: 0.5, color, '&.Mui-checked': { color } }}
        />
        <Box onClick={onClick} sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flex: 1, minWidth: 0 }}>
          <Avatar sx={{ width: 32, height: 32, bgcolor: alpha(meta.color, 0.12), color: meta.color, flexShrink: 0 }}>
            {meta.icon}
          </Avatar>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap' }}>
              <Typography variant="body2" sx={{
                wordBreak: 'break-word',
                color: event.completed ? 'text.secondary' : 'text.primary'
              }}>
                {event.title}
              </Typography>
              {(event.raw?.energyImpact ?? 0) > 0 && (
                <Typography variant="caption" sx={{
                  fontWeight: 600,
                  color: event.type === 'food' ? 'success.main' : 'warning.main',
                }}>
                  {event.type === 'food' ? '+' : '−'}{event.raw!.energyImpact} ккал
                </Typography>
              )}
            </Box>
            {rawInput && (
              <Typography variant="caption" color="text.secondary" sx={{
                display: 'block',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                mt: 0.25,
              }}>
                {rawInput}
              </Typography>
            )}
          </Box>
          <Typography variant="caption" color="text.secondary" sx={{ flexShrink: 0, width: 38, textAlign: 'right' }}>
            {event.time}
          </Typography>
        </Box>
      </Box>
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 0.5, pr: 0.5 }}>
        <Typography variant="caption" sx={{ fontWeight: 500, color, whiteSpace: 'nowrap' }}>
          від {name}
        </Typography>
      </Box>
    </Box>
  );
};
